import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ACTOR_PHRASES } from '../metadata/actor_sentence';
import Communicator from 'models/communicator';
import connect from 'components/lib/connect';
import ConversationItem from 'models/conversation_item';
import CustomerProfile from 'models/customer_profile';
import EditEmail from 'actions/composition/edit_email';
import EmailContent from './email_content';
import { EmailItemContextProvider } from './email_item_context';
import EmailStatus from 'models/conversation_item/email_status';
import getNormalizedEmailAddress from 'scripts/lib/get_normalized_email_address';
import { HandleHeightChange } from 'components/customer/conversation_history/virtualized/list_item';
import ItemCommError from '../item_comm_error';
import ItemContent from '../item_content';
import ItemMetadata from '../item_metadata';
import noop from 'lodash/noop';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';

export function EmailItem({ className, item, onEditEmail, profile }) {
  const { t } = useTranslation();
  const isFeatureEnabled = useIsFeatureEnabled();
  const isOutboundEnabled = isFeatureEnabled('conversation:outbound');

  return (
    <EmailItemContextProvider>
      <ItemContent item={item}>
        <ItemMetadata
          item={item}
          phrase={getAgentPhrase(item, profile)}
          status={getStatus()}
          text={getMetadataText(item, profile)}
        />
        <HandleHeightChange isManual>
          {({ onHeightChange }) => <EmailContent className={className} item={item} onHeightChange={onHeightChange} />}
        </HandleHeightChange>
        <ItemCommError
          className="emailItem-editMessage"
          item={item}
          message={t('Edit Message')}
          onClick={isOutboundEnabled ? onEditEmail : noop}
        />
      </ItemContent>
    </EmailItemContextProvider>
  );

  function getStatus() {
    const status = item.content.getStatus();
    switch (status) {
      case EmailStatus.DELIVERED:
        return 'delivered';
      case EmailStatus.SENT:
        return 'sent';
      case EmailStatus.DELIVERY_ERROR:
        return 'delivery failed';
      case EmailStatus.SENDING:
        return 'sending';
      default:
        return '';
    }
  }
}

// An agent's own email line is a whole sentence owned by AgentMetadata, so the
// verb can agree with its actor. `undefined` selects AgentMetadata's default
// "replied via <channel>", which resolves the channel to "email" on its own.
function getAgentPhrase(item, profile) {
  if (item.initiator.type !== Communicator.AGENT) {
    return undefined;
  }
  if (item.content.isForward()) {
    return ACTOR_PHRASES.FORWARDED_EMAIL;
  }
  if (isReplyToCustomer(item, profile)) {
    return undefined;
  }
  return ACTOR_PHRASES.SENT_EMAIL;
}

// Customer-initiated emails keep their own text; CustomerMetadata renders a
// customer name beside it, which needs no agreement handling.
function getMetadataText(item, profile) {
  if (item.initiator.type === Communicator.AGENT) {
    return undefined;
  }
  if (item.content.isForward()) {
    return 'forwarded an email';
  }
  if (isReplyToCustomer(item, profile)) {
    return 'replied via email';
  }
  return 'sent an email';
}

function isReplyToCustomer(item, profile) {
  if (!profile) {
    return false;
  }
  const customerEmails = profile.emails.map(email => getNormalizedEmailAddress(email.original, true));
  return item.content.isSentToAny(customerEmails);
}

EmailItem.propTypes = {
  className: PropTypes.string,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  onEditEmail: PropTypes.func.isRequired,
  onHeightChange: PropTypes.func,
  profile: PropTypes.instanceOf(CustomerProfile),
};

function mapStateToProps({ getProvider }, props) {
  const profile = getProvider('profile').get();
  return {
    ...props,
    profile,
  };
}

function mapExecuteToProps(executeAction, { item }) {
  const email = item.content;
  return {
    onEditEmail: () =>
      executeAction(EditEmail, {
        attachments: email.attachments,
        bcc: email.bcc,
        bodyHtmlStripped: email.bodyHtmlStripped,
        bodyHtml: email.bodyHtml,
        bodyPlainStripped: email.bodyPlainStripped,
        bodyPlain: email.bodyPlain,
        cc: email.cc,
        from: email.from,
        inReplyTo: email.getHeader('in-reply-to'),
        inlineAttachments: email.inlineAttachments(),
        subjectHtml: email.subject,
        to: email.to,
      }),
  };
}

const EmailItemContainer = connect(mapStateToProps, mapExecuteToProps)(EmailItem);
EmailItemContainer.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  onHeightChange: PropTypes.func,
};
export default EmailItemContainer;
